import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import './reservedLocationChart.scss';

import HistoricReservationService from '../historicReservationService';
import ReservationInsightsFilter from '../../interfaces/reservationInsightsFilter';
import LanguageProvider from '@/providers/languageProvider';
import Colors from '@/styles/colors';
import ChartUtils from './chartUtils';
import { SeriesData } from './reservedPeriodChart';
import ActuallyCenteredLoader from '@/components/loaders/actuallyCenteredLoader';
import { FilterOptions } from '../../interfaces/filterOptions';

export enum LocationType {
    floor = "floor",
    department = "department",
    place = "place"
}

interface ChartProps {
    id: string;
    filter: ReservationInsightsFilter;
    filterOptions: FilterOptions
}

export interface LocationSeriesData {
    label: string;
    value: number;
}
const ReservedLocationChart: React.FC<ChartProps> = (props) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [series, setSeries] = useState<LocationSeriesData[]>([]);
    const [selectedType, setSelectedType] = useState<LocationType>(LocationType.floor);

    const historicReservationService = new HistoricReservationService();

    useEffect(() => {
        setSeries([]);
        setLoading(true);
        fetchData(selectedType, props.filter);
    }, [props.filter.triggerUpdate, selectedType]);

    function getFloorName(floorId: string): string {
        return props.filterOptions.floors.find(f => f.id === floorId)?.displayName || floorId;
    }

    function getPlaceName(placeId: string): string {
        return props.filterOptions.workspaces.find(f => f.id === placeId)?.displayName || placeId;
    }

    function fetchData(selectedType: LocationType, filter: ReservationInsightsFilter): void {
        switch (selectedType) {
            case LocationType.floor:
                historicReservationService.getPlaceReservationsFloorPercentage(filter).then((response) => {
                    const series = response.map((item) => ({
                        label: getFloorName(item.floor),
                        value: item.percentage
                    }));
                    setSeries(series);
                    setLoading(false);
                });
                break;
            case LocationType.department:
                historicReservationService.getPlaceReservationsDepartmentPercentage(filter).then((response) => {
                    const series = response.map((item) => ({
                        label: `${item.department}`,  // Needs to be mapped to the correct name in the future
                        value: item.percentage
                    }));
                    setSeries(series);
                    setLoading(false);
                });
                break;
            case LocationType.place:
                historicReservationService.getPlaceReservationsPlacePercentage(filter).then((response) => {
                    const series = response.map((item) => ({
                        label: getPlaceName(item.place),
                        value: item.percentage
                    }));
                    setSeries(series);
                    setLoading(false);
                });
                break;
            default:
                break;
        }
    }

    function getTranslation(type: LocationType): string {
        return LanguageProvider.getTranslation(`pages.realestateutilization.charts.reservedlocationchart.${type}`);
    }

    return <div className="widget-reserved-location">
        <div className="d-flex align-items-center widget-toggle-buttons">
            <button className={"btn-toggle " + (selectedType === LocationType.floor ? "active" : "")} onClick={(): void => { setSeries([]); setSelectedType(LocationType.floor) }}>{getTranslation(LocationType.floor)}</button>
            <button className={"btn-toggle " + (selectedType === LocationType.department ? "active" : "")} onClick={(): void => { setSeries([]); setSelectedType(LocationType.department) }}>{getTranslation(LocationType.department)}</button>
            <button className={"btn-toggle " + (selectedType === LocationType.place ? "active" : "")} onClick={(): void => { setSeries([]); setSelectedType(LocationType.place) }}>{getTranslation(LocationType.place)}</button>
        </div>
        {!loading &&
            <HighchartsReact
                id={props.id}
                highcharts={Highcharts}
                options={reservedLocationChartOptions(series, getTranslation(selectedType), selectedType)}
            />
        }
        {loading && <ActuallyCenteredLoader />}
    </div>;
};

export default ReservedLocationChart;

function getMaxValuesInView(itemCount: number, selectedType: LocationType): number | undefined {

    const max = selectedType === LocationType.floor ? 10 : selectedType === LocationType.department ? 10 : selectedType === LocationType.place ? 10 : 10;
    const inViewCount = itemCount <= max ? undefined : max; // undefined means all values are shown

    return inViewCount;
}

const reservedLocationChartOptions = (data: SeriesData[], subTitle: string, selectedType: LocationType): Highcharts.Options => ({
    chart: {
        type: 'bar',
    },
    title: ChartUtils.getTitle(`${LanguageProvider.getTranslation("pages.realestateutilization.charts.reservedlocationchart.title")} (${subTitle})`),
    xAxis: {
        min: 0,
        max: getMaxValuesInView(data.length, selectedType),
        categories: data.map(d => `${d.label}`),
        title: {
            text: '',
        },
        scrollbar: {
            enabled: !!getMaxValuesInView(data.length, selectedType),
        },
        lineWidth: 0,
        labels: {
            style: {

            },
            useHTML: true,
            formatter: function (): string {
                return `<span class="locationbarchart-custom-label">${this.value}</span>`;
            }
        },
    },
    yAxis: {
        min: 0,
        max: 100,
        title: {
            text: '',
        },
        tickInterval: 25,
        labels: {
            formatter: function (): string {
                return `${this.value}%`;
            },
        }
    },
    series: [
        {
            type: 'bar',
            data: data.map(d => Math.round(d.value)),
            showInLegend: false,
            color: Colors.shakespeare_blue,
            pointPadding: 0.04,
            groupPadding: 0.1,
            dataLabels: {
                enabled: true,
                format: '{point.y}%'
            }
        },
    ],
    tooltip: {
        formatter: function () {
            return `<b>${this.key}</b>: ${this.y}%`;
        }
    },
    credits: {
        enabled: false
    },
    accessibility: {
        enabled: false
    },
    lang: ChartUtils.getLanguageOptions(),
    exporting: ChartUtils.getExportingOptions(),
});